import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Settings, Save, Loader2, CreditCard, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

function PaymentProvidersCard() {
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    api.get('/admin/payments/status').then(r => setStatus(r.data)).catch(() => setStatus({}));
  }, []);

  const testPayPal = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post('/admin/payments/paypal/test-connection');
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || e.message });
    } finally {
      setTesting(false);
    }
  };

  if (!status) return null;
  const paypal = status.paypal || {};
  const stripe = status.stripe || {};

  const dot = (ok) => ok
    ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
    : <XCircle className="h-4 w-4 text-stone-400" />;

  return (
    <Card className="bg-white border-stone/50" data-testid="payment-providers-card">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-ink" />
          <h3 className="text-xs uppercase tracking-wider text-muted-foreground">Payment providers</h3>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Stripe */}
          <div className="rounded-md border border-stone/40 p-3 space-y-2" data-testid="stripe-status-panel">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Card (Stripe)</span>
              {dot(stripe.configured)}
            </div>
            <div className="text-[11px] text-muted-foreground space-y-1">
              <div>Mode: <span className="font-mono text-ink">{stripe.mode || '—'}</span></div>
              <div>Publishable key: {stripe.publishable_prefix || (stripe.publishable_present ? 'set' : 'missing')}</div>
              <div>Secret key: {stripe.secret_prefix || 'missing'}</div>
              <div>Webhook secret: {stripe.webhook_secret_present ? 'set' : 'missing'}</div>
              {/* Feb 2026: show env vs CMS source explicitly so admins can
                  pinpoint which source is stale when the effective key
                  disagrees with their expectations. */}
              {(stripe.env_secret_prefix || stripe.cms_secret_prefix || stripe.env_publishable_prefix || stripe.cms_publishable_prefix) && (
                <div className="mt-2 pt-2 border-t border-stone/30 space-y-0.5 text-[10px]">
                  <div className="text-muted-foreground font-semibold uppercase tracking-wider mb-1">Source breakdown</div>
                  <div>env secret: <span className="font-mono">{stripe.env_secret_prefix || '(empty)'}</span></div>
                  <div>cms secret: <span className="font-mono">{stripe.cms_secret_prefix || '(empty)'}</span></div>
                  <div>env publishable: <span className="font-mono">{stripe.env_publishable_prefix || '(empty)'}</span></div>
                  <div>cms publishable: <span className="font-mono">{stripe.cms_publishable_prefix || '(empty)'}</span></div>
                </div>
              )}
            </div>
            {(() => {
              // Feb 2026 diagnostic: shout when the keys aren't a matched
              // live pair so admins don't ship a live site on test keys.
              const sk = (stripe.secret_prefix || '').toLowerCase();
              const pk = (stripe.publishable_prefix || '').toLowerCase();
              const cmsSk = (stripe.cms_secret_prefix || '').toLowerCase();
              const cmsPk = (stripe.cms_publishable_prefix || '').toLowerCase();
              const secretIsTest = sk.startsWith('sk_test');
              const pubIsTest = pk.startsWith('pk_test');
              const secretIsLive = sk.startsWith('sk_live');
              const pubIsLive = pk.startsWith('pk_live');
              // Stale CMS entry: effective key is live but CMS holds a test
              // value (unused). Warn so the admin cleans it up.
              const cmsSkStale = cmsSk.startsWith('sk_test') && secretIsLive;
              const cmsPkStale = cmsPk.startsWith('pk_test') && pubIsLive;
              const anyTest = secretIsTest || pubIsTest;
              const mismatched = (secretIsLive && pubIsTest) || (secretIsTest && pubIsLive);
              if (!anyTest && !mismatched && !cmsSkStale && !cmsPkStale) return null;
              return (
                <div className="rounded p-2 text-[11px] bg-red-50 border border-red-300 text-red-800" data-testid="stripe-mode-warning">
                  <div className="font-semibold mb-1">
                    {mismatched ? 'Stripe key pair mismatch'
                      : anyTest ? 'Stripe is in TEST mode — customers cannot pay'
                      : 'Stale test key stored in CMS'}
                  </div>
                  <div>
                    {secretIsTest && <div>• <strong>Secret key</strong> starts with <code>sk_test_</code>. Replace <code>STRIPE_SECRET_KEY</code> in production <em>Imports</em> with your <code>sk_live_…</code> key, <strong>OR</strong> paste it into the <em>Payments → payments.stripe_secret_key</em> setting below (takes effect immediately, no redeploy).</div>}
                    {pubIsTest && <div>• <strong>Publishable key</strong> starts with <code>pk_test_</code>. Replace it below (Payments → <code>payments.stripe_publishable_key</code>) with your <code>pk_live_…</code> key.</div>}
                    {cmsSkStale && <div>• A <strong>test secret key</strong> (<code>sk_test_</code>) is stored in <em>payments.stripe_secret_key</em> but env has a live key. Clear the CMS setting to remove ambiguity.</div>}
                    {cmsPkStale && <div>• A <strong>test publishable key</strong> (<code>pk_test_</code>) is stored in <em>payments.stripe_publishable_key</em> but env has a live key. Clear the CMS setting to remove ambiguity.</div>}
                  </div>
                  <div className="mt-1.5 text-[10px]">
                    Get the matched pair from <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="underline">Stripe Dashboard → Developers → API keys</a>. Make sure you copy the keys from the <strong>live</strong> section (starts with <code>sk_live_</code> / <code>pk_live_</code>), not the test section.
                  </div>
                </div>
              );
            })()}
          </div>

          {/* PayPal */}
          <div className="rounded-md border border-stone/40 p-3 space-y-2" data-testid="paypal-status-panel">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">PayPal</span>
              {dot(paypal.configured)}
            </div>
            <div className="text-[11px] text-muted-foreground space-y-1">
              <div>Mode: <span className="font-mono text-ink">{paypal.mode || 'sandbox'}</span></div>
              <div>Client ID: {paypal.client_id_present ? 'set' : 'missing'}</div>
              <div>Client secret: {paypal.client_secret_present ? 'set' : 'missing'}</div>
              <div>Webhook id: {paypal.webhook_id_present ? 'set' : 'missing'}</div>
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={!paypal.configured || testing}
              onClick={testPayPal}
              className="w-full h-7 text-xs mt-2"
              data-testid="paypal-test-connection-btn"
            >
              {testing ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Testing…</> : 'Test connection'}
            </Button>
            {testResult && (
              <div
                className={`text-[11px] rounded p-2 ${testResult.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}
                data-testid="paypal-test-connection-result"
              >
                {testResult.ok
                  ? `✓ Live PayPal token issued (expires in ${testResult.expires_in}s)`
                  : `✗ ${testResult.error || testResult.hint || 'Unknown error'}`}
              </div>
            )}
            {!paypal.configured && (
              <p className="text-[10px] text-muted-foreground italic">
                Add PAYPAL_CLIENT_ID + PAYPAL_CLIENT_SECRET to backend/.env, then restart the backend.
                Setup guide: <span className="font-mono">/app/docs/paypal-setup-checklist.md</span>
              </p>
            )}
          </div>
        </div>

        <div className="text-[10px] text-muted-foreground">
          Live buyers see: {status.available_providers?.length
            ? status.available_providers.map(p => p === 'stripe' ? 'Credit or debit card' : (p === 'paypal' ? 'PayPal' : p)).join(', ')
            : 'no live providers configured — mock only'}
        </div>
      </CardContent>
    </Card>
  );
}

export default function SettingsPage() {
  const [settings, setSettings] = useState([]);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try { const r = await api.get('/admin/settings'); setSettings(r.data); } catch {} finally { setLoading(false); }
  };

  const handleChange = (key, value) => { setEdits({...edits, [key]: value}); };

  const handleSave = async (key) => {
    try { await api.put(`/admin/settings/${key}`, { value: edits[key] }); toast.success(`${key} saved`); const newEdits = {...edits}; delete newEdits[key]; setEdits(newEdits); await loadSettings(); } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  const groups = {
    'Site': settings.filter(s => s.key.startsWith('site.')),
    'Contact': settings.filter(s => s.key.startsWith('contact.')),
    'Notifications': settings.filter(s => s.key.startsWith('notifications.')),
    'Payments': settings.filter(s => s.key.startsWith('payments.')),
    'Business Hours': settings.filter(s => s.key.startsWith('business.')),
    'Upload': settings.filter(s => s.key.startsWith('upload.')),
    'Mailed Documents': settings.filter(s => s.key.startsWith('mailed_doc.')),
    'Analytics': settings.filter(s => s.key.startsWith('analytics.')),
    'Navigation': settings.filter(s => s.key.startsWith('nav.') || s.key.startsWith('footer.')),
  };

  return (
    <div data-testid="settings-page">
      <div className="flex items-center gap-2 mb-4"><Settings className="h-4 w-4 text-ink" /><h1 className="text-base font-semibold text-ink">Settings</h1></div>
      <div className="space-y-4">
        <PaymentProvidersCard />
        {Object.entries(groups).map(([group, items]) => items.length > 0 && (
          <Card key={group} className="bg-white border-stone/50">
            <CardContent className="p-4">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">{group}</h3>
              <div className="space-y-3">
                {items.map(s => {
                  const val = edits[s.key] !== undefined ? edits[s.key] : (s.value || '');
                  const changed = edits[s.key] !== undefined;
                  return (
                    <div key={s.key} className="flex items-center gap-3">
                      <div className="w-48 flex-shrink-0"><Label className="text-xs font-mono">{s.key}</Label>{s.description && <p className="text-[9px] text-muted-foreground">{s.description}</p>}</div>
                      <Input value={val} onChange={e => handleChange(s.key, e.target.value)} className="h-8 text-xs flex-1" />
                      {changed && <Button size="sm" className="h-7 text-xs gap-1" onClick={() => handleSave(s.key)}><Save className="h-3 w-3" /> Save</Button>}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
